import React, { useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import classNames from "classnames";
import { formatISO } from "date-fns";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Entity } from "@common/types/entity";
import { useLockDate } from "@api/lock.api";
import { LockIcon, OpenLockIcon } from "@icons";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { formatLockDateMessage } from "./formatLockDateMessage";
import styles from "./WorkspaceLock.module.scss";

const LockStatusCellComponent: React.FC<
    CellContext<ViewTableFeatures, Entity, number>
> = ({ row }) => {
    const lockDate = useLockDate(row.original.id);

    const lockDateString = useMemo(
        () =>
            lockDate ? formatISO(lockDate, { representation: "date" }) : null,
        [lockDate],
    );

    const tooltip = useMemo(
        () => formatLockDateMessage(lockDateString),
        [lockDateString],
    );

    return (
        <ViewTableCell>
            <Tooltip content={tooltip}>
                <div
                    className={classNames(styles.statusIcon, {
                        [styles.locked]: lockDateString,
                        [styles.unlocked]: !lockDateString,
                    })}
                >
                    {lockDateString ? (
                        <LockIcon className="icon-color-current" />
                    ) : (
                        <OpenLockIcon className="icon-color-current" />
                    )}
                </div>
            </Tooltip>
        </ViewTableCell>
    );
};

export const LockStatusCell = React.memo(LockStatusCellComponent);
LockStatusCell.displayName = "LockStatusCell";
